import styled from "styled-components";
import type {CSSProperties, RefObject} from "react";
import {useCallback, useLayoutEffect, useRef, useState} from "react";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import type {XyiconFeature} from "../../../generated/api/base";
import type {IModel} from "../../../data/models/Model";
import {radius, VerticalFlexStyle, zIndex} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import {ReactUtils} from "../../utils/ReactUtils";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {SpotlightBodyContentV5} from "./SpotlightContentV5";

interface ISpotlightPopupV5Props {
	readonly parentRef: RefObject<HTMLDivElement>;
	readonly verticalAlignment?: VerticalAlignment;
	readonly horizontalAlignment?: HorizontalAlignment;
	readonly centerOnScreen?: boolean;
	readonly height?: string;
	readonly className?: string;
	readonly offsetX?: number;
	readonly offsetY?: number;
	readonly closeOnClickOutside?: boolean;
	readonly onClick?: (e?: React.MouseEvent) => void;
	readonly onClose?: (e?: React.MouseEvent | KeyboardEvent) => void;
	readonly onMouseOver?: (e?: React.MouseEvent) => void;
	readonly feature: XyiconFeature;
	readonly items: IModel[];
	readonly openInSpaceEditor?: boolean;
}

interface IPopupStyledProps {
	$center: boolean;
	$width: string;
	$height: string;
}

export const SpotlightPopupV5 = (props: ISpotlightPopupV5Props) => {
	const _floating = useRef<HTMLDivElement>(null);

	const {
		parentRef,
		verticalAlignment = VerticalAlignment.bottom,
		horizontalAlignment = HorizontalAlignment.outerRight,
		offsetY = -8,
		offsetX = 16,
		className = "",
		closeOnClickOutside = true,
		centerOnScreen,
		height,
		onClose,
		onClick,
		onMouseOver,
		feature,
		items,
		openInSpaceEditor = false,
	} = props;

	const [transform, setTransform] = useState<TransformObj>(null);
	const [styleState, setStyleState] = useState<CSSProperties>({});

	const handleClose = useCallback(() => {
		if (closeOnClickOutside) {
			onClose?.();
		}
	}, [closeOnClickOutside, onClose]);

	const onCloseOnEscape = useCallback(
		(e: KeyboardEvent) => {
			e.stopImmediatePropagation();
			onClose?.();
		},
		[onClose],
	);

	// Protect the add rule popup, button container, and select dropdowns so clicking them doesn't close the spotlight popup
	useClickOutside([_floating], handleClose, "mousedown");
	useCloseOnKeyboardPress(onCloseOnEscape, KeyboardListener.KEY_ESCAPE, [], {
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	const onClickPopup = (e: React.MouseEvent) => {
		e.stopPropagation();
		onClick?.();
	};

	const setMarginsCallback = useCallback(() => {
		if (centerOnScreen && _floating.current) {
			const newStyleState: CSSProperties = {
				marginLeft: `${(document.body.offsetWidth - _floating.current.offsetWidth) / 2}px`,
				marginTop: `${(document.body.offsetHeight - _floating.current.offsetHeight) / 2}px`,
				width: "660px",
				maxHeight: "592px",
			};
			if (!ObjectUtils.compare(styleState, newStyleState)) {
				setStyleState(newStyleState);
			}
		}
	}, [centerOnScreen, styleState]);

	const setTransformCallback = useCallback(() => {
		if (parentRef && _floating.current) {
			const newTransform = DomUtils.getFixedFloatingElementPosition(
				parentRef.current,
				_floating.current,
				verticalAlignment,
				horizontalAlignment,
				offsetY,
				offsetX,
			);

			if (!ObjectUtils.compare(transform, newTransform)) {
				setTransform(newTransform);
			}
		}
	}, [parentRef, verticalAlignment, horizontalAlignment, offsetY, offsetX, transform]);

	useLayoutEffect(() => {
		setMarginsCallback();
		setTransformCallback();
	}, [setMarginsCallback, setTransformCallback]);

	const popupStyle: CSSProperties = {
		visibility: centerOnScreen || transform?.translate ? "visible" : "hidden",
		...styleState,
	};

	if (!centerOnScreen && transform) {
		popupStyle.left = `${transform.x}px`;
		popupStyle.top = `${transform.y}px`;
	}

	return (
		<SpotlightPopupStyled
			ref={_floating}
			style={popupStyle}
			$center={centerOnScreen}
			$width={"660px"}
			$height={height}
			className={ReactUtils.cls(className)}
			onClick={onClickPopup}
			onMouseOver={onMouseOver}
		>
			<SpotlightBodyContentV5
				feature={feature}
				items={items}
				openInSpaceEditor={openInSpaceEditor}
			/>
		</SpotlightPopupStyled>
	);
};

const SpotlightPopupStyled = styled.div<IPopupStyledProps>`
	${VerticalFlexStyle};
	background-color: ${colorPalette.white};
	border-radius: ${radius.md};
	max-width: 670px;
	width: 666px;
	box-shadow: 0px 4px 20px 0px #00000033;
	z-index: ${zIndex.createPanel + 1};
	position: fixed;
	visibility: hidden;
	max-height: 592px;
	overflow-y: hidden;
`;
